import React, { useState } from 'react';
import { 
  ShieldAlert, 
  Users, 
  Leaf, 
  BarChart3, 
  CheckSquare, 
  Globe, 
  Camera, 
  Share2, 
  Instagram, 
  Twitter,
  Facebook,
  Hash, 
  Mail,
  Send,
  ArrowRight,
  Heart,
  Zap,
  UserPlus,
  AlertCircle,
  Check,
  X,
  FileText,
  Download
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { Card, Badge, Button } from '../components/ui';

// Campaign metrics
const campaignStats = [
  { label: 'Pledges', value: '3,720', icon: <CheckSquare className="h-5 w-5 text-avn-purple-600" /> },
  { label: 'Communities Protected', value: '47', icon: <Users className="h-5 w-5 text-avn-purple-600" /> },
  { label: 'Farmers Supported', value: '1,250', icon: <Leaf className="h-5 w-5 text-green-600" /> },
  { label: 'Social Media Reach', value: '2.8M', icon: <Globe className="h-5 w-5 text-blue-600" /> },
];

// Exploitation types
const exploitationTypes = [
  { 
    title: 'Child Labor',
    description: 'Over 160 million children worldwide are engaged in child labor, with 70% working in agriculture.',
    icon: <Users className="h-6 w-6 text-amber-600" />,
    color: 'bg-amber-50 border-amber-200'
  },
  { 
    title: 'Forced Labor',
    description: 'Millions of adults and children are forced to work in agriculture through violence, threats, or debt bondage.',
    icon: <AlertCircle className="h-6 w-6 text-red-600" />,
    color: 'bg-red-50 border-red-200'
  },
  { 
    title: 'Wage Theft',
    description: 'Agricultural workers often receive less than minimum wage and are denied rightful pay through various schemes.',
    icon: <BarChart3 className="h-6 w-6 text-avn-purple-600" />,
    color: 'bg-avn-purple-50 border-avn-purple-200'
  },
  { 
    title: 'Unsafe Conditions',
    description: 'Exposure to harmful pesticides, dangerous machinery, and extreme weather without proper protection.',
    icon: <ShieldAlert className="h-6 w-6 text-orange-600" />,
    color: 'bg-orange-50 border-orange-200'
  },
];

// Testimonials
const testimonials = [
  {
    quote: "As a farmer who has experienced exploitation firsthand, I'm proud to support #NotOnOurPlates. This campaign gives farmers like me a voice and hope for a more equitable future.",
    name: "Edward Mutesa",
    title: "Coffee Farmer, Uganda",
    image: "https://images.unsplash.com/photo-1595774047460-bebac5b9e998?q=80&w=200&h=200&auto=format&fit=crop"
  },
  {
    quote: "By participating in #NotOnOurPlates, our organization is able to ensure that the food we source is truly ethical. The transparency tools AVN provides have been game-changing.",
    name: "Sarah Johnson",
    title: "Food Service Director, UK",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=200&h=200&auto=format&fit=crop"
  },
  {
    quote: "The exploitation in our food system has been hidden for too long. #NotOnOurPlates is finally bringing these issues to light and giving consumers the power to make ethical choices.",
    name: "Michael Chen",
    title: "Consumer Advocate",
    image: "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?q=80&w=200&h=200&auto=format&fit=crop"
  }
];

const NotOnOurPlatesPage: React.FC = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) {
      setSubscribeError('Please provide your name and email');
      return;
    }
    
    setSubmitting(true);
    setSubscribeError('');
    
    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setSubscribeSuccess(true);
      
      // Reset form
      setEmail('');
      setName('');
      
      // Reset success message after delay
      setTimeout(() => {
        setSubscribeSuccess(false);
      }, 5000);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-red-900 to-red-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1506617564039-2f3b650b7010" 
            alt="Agricultural worker in field" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 py-20 relative z-10">
          <AnimatedSection>
            <div className="max-w-4xl">
              <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm w-fit px-4 py-2 rounded-full text-white mb-6">
                <Hash className="h-5 w-5" />
                <span className="font-medium">NotOnOurPlates</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Taking a Stand Against Exploitation in Food Production
              </h1>
              
              <p className="text-xl opacity-90 mb-8 leading-relaxed">
                We believe that no food produced through human exploitation should end up on our plates. 
                Join our movement to create a more ethical and transparent food system.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="bg-white text-red-900 border-white hover:bg-white/90"
                >
                  <a href="#pledge" className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5" />
                    <span>Take the Pledge</span>
                  </a>
                </Button>
                <Button 
                  variant="outline"  
                  size="lg"
                  className="bg-transparent text-white border-white hover:bg-white/10"
                >
                  <a href="#learn" className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    <span>Learn More</span>
                  </a>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 to-transparent"></div>
      </div>

      {/* Campaign Stats */}
      <div className="container mx-auto px-4 -mt-16 relative z-20 mb-20">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {campaignStats.map((stat, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="text-center">
                  <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold mb-1">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>

      {/* About the Campaign */}
      <div className="container mx-auto px-4 py-16" id="learn">
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge 
              variant="red" 
              className="mb-4"
              icon={<ShieldAlert className="h-4 w-4" />}
            >
              The Campaign
            </Badge>
            <h2 className="text-4xl font-bold mb-6">Why #NotOnOurPlates?</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              The food on our plates should nourish not only our bodies but also the communities that produce it. 
              Yet much of our global food supply is tainted by exploitation. #NotOnOurPlates is AVN's campaign to 
              expose these hidden abuses and create actionable paths toward a more ethical food system.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <AnimatedSection delay={0.2}>
            <div className="relative h-96 rounded-xl overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1605000797499-95a51c5269ae" 
                alt="Agricultural workers" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                <div className="p-6">
                  <Badge variant="white" className="mb-2">
                    Our Mission
                  </Badge>
                  <p className="text-white text-lg">
                    Creating a food system where exploitation has no place
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-3">Campaign Goals</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="p-1 bg-green-100 rounded-full mt-1">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <span className="font-medium">Raise Awareness</span>
                      <p className="text-gray-600">Educate consumers, businesses, and policymakers about exploitation in food production</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 bg-green-100 rounded-full mt-1">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <span className="font-medium">Drive Accountability</span>
                      <p className="text-gray-600">Push for greater transparency and verification in agricultural supply chains</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 bg-green-100 rounded-full mt-1">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <span className="font-medium">Create Alternatives</span>
                      <p className="text-gray-600">Build and support ethical food production systems that fairly compensate all workers</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 bg-green-100 rounded-full mt-1">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <span className="font-medium">Empower Change</span>
                      <p className="text-gray-600">Provide consumers with tools to make informed choices about their food</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Forms of Exploitation */}
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge 
              variant="red" 
              className="mb-4"
              icon={<AlertCircle className="h-4 w-4" />}
            >
              The Problem
            </Badge>
            <h2 className="text-4xl font-bold mb-6">Forms of Exploitation in Agriculture</h2>
            <p className="text-lg text-gray-600">
              Exploitation in agriculture takes many forms, often hidden from consumers. 
              Here are some of the most prevalent issues we're fighting against:
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-6 mb-20">
          {exploitationTypes.map((item, index) => (
            <AnimatedSection key={index} delay={index * 0.1}>
              <div className={`rounded-xl p-6 border ${item.color}`}>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white rounded-full shadow-sm mt-1">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-700">{item.description}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* AVN's Approach */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-20">
          <AnimatedSection>
            <div className="text-center mb-12">
              <Badge 
                variant="purple" 
                className="mb-4"
                icon={<Zap className="h-4 w-4" />}
              >
                Our Solution
              </Badge>
              <h2 className="text-4xl font-bold mb-6">How AVN is Fighting Exploitation</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Through our innovative technology and partnerships, we're creating a future where all food is verifiably free from exploitation.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            <AnimatedSection delay={0.1}>
              <Card className="h-full">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-avn-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShieldAlert className="h-8 w-8 text-avn-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Verification Technology</h3>
                  <p className="text-gray-600">
                    Our proprietary AI system monitors and verifies ethical practices throughout the entire supply chain, creating transparency from farm to table.
                  </p>
                </div>
              </Card>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <Card className="h-full">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Community Empowerment</h3>
                  <p className="text-gray-600">
                    We work directly with farming communities to develop ownership models that ensure fair compensation and improve livelihoods.
                  </p>
                </div>
              </Card>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <Card className="h-full">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Globe className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Advocacy & Education</h3>
                  <p className="text-gray-600">
                    We advocate for policy changes and educate consumers about the importance of choosing ethically-produced food.
                  </p>
                </div>
              </Card>
            </AnimatedSection>
          </div>
        </div>

        {/* Take Action Section */}
        <div id="pledge" className="mb-20">
          <AnimatedSection>
            <div className="bg-gradient-to-r from-red-900 to-red-800 rounded-xl text-white p-8">
              <div className="text-center mb-12">
                <Badge 
                  variant="white" 
                  className="mb-4 bg-white/20 text-white"
                  icon={<Heart className="h-4 w-4" />}
                >
                  Take Action
                </Badge>
                <h2 className="text-4xl font-bold mb-6">Join the #NotOnOurPlates Movement</h2>
                <p className="text-xl opacity-90 max-w-3xl mx-auto">
                  There are multiple ways you can support our campaign and help create a food system free from exploitation.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4">Take the Pledge</h3>
                  <p className="mb-6 opacity-90">
                    Commit to making conscious food choices and supporting ethical supply chains.
                  </p>
                  
                  <form onSubmit={handleSubscribe} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Your Name
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg placeholder-white/60 text-white focus:ring-2 focus:ring-white/50 focus:outline-none"
                        placeholder="Enter your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg placeholder-white/60 text-white focus:ring-2 focus:ring-white/50 focus:outline-none"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    
                    {subscribeError && (
                      <div className="bg-red-100 text-red-700 p-3 rounded-lg flex items-center gap-2">
                        <X className="h-4 w-4" />
                        <span>{subscribeError}</span>
                      </div>
                    )}
                    
                    {subscribeSuccess && (
                      <div className="bg-green-100 text-green-700 p-3 rounded-lg flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        <span>Thank you for taking the pledge!</span>
                      </div>
                    )}
                    
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-white text-red-900 py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-white/90 transition-colors disabled:opacity-70"
                    >
                      {submitting ? (
                        <>
                          <motion.div 
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          >
                            <Send className="h-5 w-5" />
                          </motion.div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <CheckSquare className="h-5 w-5" />
                          <span>Take the Pledge</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-white/20 rounded-full mt-1">
                        <Share2 className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">Spread the Word</h3>
                        <p className="mb-4 opacity-90">
                          Help raise awareness by sharing the campaign with your networks.
                        </p>
                        <div className="flex gap-3">
                          <a 
                            href="https://twitter.com/intent/tweet?text=I'm%20supporting%20%23NotOnOurPlates%20to%20fight%20exploitation%20in%20our%20food%20system.%20Join%20me!%20https://www.avnearth.com/not-on-our-plates"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                          >
                            <Twitter className="h-5 w-5" />
                          </a>
                          <a 
                            href="https://www.facebook.com/sharer/sharer.php?u=https://www.avnearth.com/not-on-our-plates"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                          >
                            <Facebook className="h-5 w-5" />
                          </a>
                          <a 
                            href="https://www.instagram.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                          >
                            <Instagram className="h-5 w-5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-white/20 rounded-full mt-1">
                        <Camera className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">Join the Campaign</h3>
                        <p className="mb-4 opacity-90">
                          Share photos of your ethical food choices with #NotOnOurPlates
                        </p>
                        <button className="flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg">
                          <Hash className="h-4 w-4" />
                          <span>NotOnOurPlates</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-white/20 rounded-full mt-1">
                        <UserPlus className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">Become an Advocate</h3>
                        <p className="mb-4 opacity-90">
                          Help us amplify the message by becoming a community advocate.
                        </p>
                        <Link to="/signup" className="flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg w-fit">
                          <span>Learn More</span>
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Testimonials */}
        <div className="mb-20">
          <AnimatedSection>
            <div className="text-center mb-12">
              <Badge 
                variant="purple" 
                className="mb-4"
                icon={<Users className="h-4 w-4" />}
              >
                Voices of Change
              </Badge>
              <h2 className="text-4xl font-bold mb-6">What Our Supporters Say</h2>
            </div>
          </AnimatedSection>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="overflow-hidden h-80">
                {testimonials.map((testimonial, idx) => (
                  <motion.div
                    key={idx}
                    className="absolute inset-0"
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: activeTestimonial === idx ? 1 : 0,
                      x: activeTestimonial === idx ? 0 : (activeTestimonial > idx ? -100 : 100)
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <Card className="h-full flex flex-col items-center text-center p-8">
                      <div className="w-20 h-20 rounded-full overflow-hidden mb-6 border-4 border-gray-50">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <p className="text-lg italic text-gray-700 mb-6">
                        "{testimonial.quote}"
                      </p>
                      <div className="mt-auto">
                        <p className="font-bold text-gray-800">{testimonial.name}</p>
                        <p className="text-gray-600">{testimonial.title}</p>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
              
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      activeTestimonial === idx 
                        ? 'bg-avn-purple-600' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`View testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Contact Section */}
        <div className="mb-20">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="bg-avn-purple-900 text-white p-8">
                <AnimatedSection>
                  <h2 className="text-3xl font-bold mb-6">Get Involved</h2>
                  <p className="mb-6">
                    Want to learn more about the #NotOnOurPlates campaign or discuss partnership opportunities? 
                    Get in touch with our campaign team.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-full">
                        <Mail className="h-5 w-5" />
                      </div>
                      <a href="mailto:notonourplates@avnearth.com" className="hover:underline">
                        notonourplates@avnearth.com
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-full">
                        <Hash className="h-5 w-5" />
                      </div>
                      <span>NotOnOurPlates</span>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
              
              <div className="p-8">
                <AnimatedSection delay={0.2}>
                  <h3 className="text-2xl font-bold mb-6">Contact Us</h3>
                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Your Name
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                        placeholder="Tell us how you'd like to get involved..."
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      className="bg-avn-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-avn-purple-700 transition-colors"
                    >
                      Send Message
                    </button>
                  </form>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </div>
        
        {/* Resources Section */}
        <div>
          <AnimatedSection>
            <div className="text-center mb-12">
              <Badge 
                variant="green" 
                className="mb-4"
                icon={<FileText className="h-4 w-4" />}
              >
                Resources
              </Badge>
              <h2 className="text-4xl font-bold mb-6">Campaign Resources</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Access materials to help you promote the #NotOnOurPlates campaign and educate others about exploitation in agriculture.
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-6">
            <AnimatedSection delay={0.1}>
              <Card className="h-full flex flex-col">
                <h3 className="text-xl font-bold mb-4">Campaign Toolkit</h3>
                <p className="text-gray-600 mb-6">
                  Download our comprehensive toolkit with social media templates, fact sheets, presentation slides, and more.
                </p>
                <button className="mt-auto bg-avn-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-avn-purple-700 transition-colors flex items-center gap-2 justify-center">
                  <Download className="h-5 w-5" />
                  <span>Download Toolkit</span>
                </button>
              </Card>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <Card className="h-full flex flex-col">
                <h3 className="text-xl font-bold mb-4">Educational Materials</h3>
                <p className="text-gray-600 mb-6">
                  Access lesson plans, activities, and infographics designed for schools, community groups, and organizations.
                </p>
                <button className="mt-auto bg-avn-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-avn-purple-700 transition-colors flex items-center gap-2 justify-center">
                  <Download className="h-5 w-5" />
                  <span>Get Materials</span>
                </button>
              </Card>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <Card className="h-full flex flex-col">
                <h3 className="text-xl font-bold mb-4">Research Reports</h3>
                <p className="text-gray-600 mb-6">
                  Explore our in-depth research on exploitation in agriculture and the impact of ethical sourcing.
                </p>
                <Link 
                  to="/publications" 
                  className="mt-auto bg-avn-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-avn-purple-700 transition-colors flex items-center gap-2 justify-center"
                >
                  <FileText className="h-5 w-5" />
                  <span>View Publications</span>
                </Link>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotOnOurPlatesPage;